/**
 * GuestProfileModal Component
 * Allows guests to view and edit their display name
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/guestProfileModal.css';

interface GuestProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  onSave: (newName: string) => void;
}

const GuestProfileModal: React.FC<GuestProfileModalProps> = ({
  isOpen,
  onClose,
  currentName,
  onSave
}) => {
  const [name, setName] = useState(currentName);
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === currentName) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      onSave(name.trim());
      onClose();
    } catch (error) {
      console.error('Failed to save guest profile:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <div className="guest-profile-overlay" onClick={onClose}>
      <div className="guest-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="guest-profile-header">
          <h3>Guest Profile</h3>
          <ModalCloseButton onClick={onClose} />
        </div>
        
        <form onSubmit={handleSubmit} className="guest-profile-form">
          <div className="form-group">
            <label htmlFor="guest-name">Display Name</label>
            <input
              id="guest-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="name-input"
              autoFocus
              required
              maxLength={50}
            />
            <p className="form-hint">This name will be shown to others when you add or edit items</p>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-cancel"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={isSaving || !name.trim()}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default GuestProfileModal;
